/**
 * Object composer, is function that builds object with aggregated
 * attributes/behaviors to a provided object (or state) and composing
 * to acquire specific behaviors
 *
 * @see https://www.youtube.com/watch?v=wfMtDGfHWpA
 */
import { miner } from '@cbre/digger';
import { addition, makeAmountWith, reduceAmountWith, ZERO_SF } from '@shared/utils/helpers/uom-calculator';
import { Amount, Unit } from 'uom';
import { Quantity, Units } from 'uom-units';

/**
 * Composer to wrap the usage of `miner` while retrieving information in
 * specified way
 * @see miner
 */
export interface WithDataDef {
  /**
   * uses miner to retrieve data from host object based on key (target),
   * value (source) object
   * @param dataset
   */
  getData: (dataset: { [key: string]: string }) => { [key: string]: any };
}

/**
 * Composer function for WithDataDef
 */
export const withData = (): WithDataDef => {
  function getData(dataset) {
    return miner(this, dataset);
  }
  return { getData };
};

/**
 * Composer to add simplified way to retrieve the `id` value of an entity
 * with `{ entity: { id, ...} }`
 * structure
 */
export interface WithIdRetrievalDef {
  /**
   * Returns the value of id in an object having the next structure
   * `{ enityt: { id} }`
   * @returns {number} entity id
   */
  getId: () => number;
}

/**
 * composer function for WithIdRetrieval
 */
export const withIdRetrieval = (): WithIdRetrievalDef => {
  function getId() {
    return this.entity ? this.entity.id : this.id;
  }

  return { getId };
};

/** WithUoms composer interface */
export interface WithUomsDef {
  /**
   * Returns a composed field of numeric + uom in the shape of Amount
   * @param {string} field name for the numeric value
   * @param {string} field name for the uom
   * @returns Amount
   */
  getAsAmount: (
    valuePath: string,
    uomPath: string,
    defaultUnit?: Unit.Unit<Quantity.Area>
  ) => Amount.Amount<Quantity.Area | Quantity.Length>;
}

/**
 * Composer to add behavior to read uom fields as `Amount.Amount`
 * @see Amount (uom package)
 */
export const withUoms: () => WithUomsDef = () => {
  function getAsAmount(
    valuePath: string,
    uomPath: string,
    defaultUnit = Units.SquareFeet
  ): Amount.Amount<Quantity.Area | Quantity.Length> {
    const { value, uom } = miner(this, { value: valuePath, uom: uomPath });
    if (value == null && uom == null) {
      return null;
    }
    return makeAmountWith({ value, uom: uom || defaultUnit.name, defaultUnit });
  }
  return { getAsAmount };
};

/**
 * Behavioral aggregation to sum `total_vacant_area` with it's respective `uom`
 * from all available usages
 */
export const withVacantArea = () => {
  function getVacantArea() {
    return reduceAmountWith(this.usages || [], {
      dataset: { value: 'total_vacant_area', uom: 'total_vacant_area_uom_desc' },
      operation: addition,
      initialValue: ZERO_SF,
    });
  }
  return { getVacantArea };
};

/**
 * Behavioral aggregation to sum `total_available_surface_area` with it's respective `uom`
 * from all available usages
 */
export const withAvailableArea = () => {
  function getAvailableArea() {
    return reduceAmountWith(this.usages || [], {
      dataset: { value: 'total_available_surface_area', uom: 'total_available_surface_area_uom_desc' },
      operation: addition,
      initialValue: ZERO_SF,
    });
  }
  return { getAvailableArea };
};
